import React from "react";
import { View, Text, StyleSheet, Platform } from "react-native";
import { Feather } from "@expo/vector-icons";
import InputDate from "../InputDate";
import Colors from "../../constants/Colors";
import {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormSetError,
  UseFormClearErrors,
} from "react-hook-form";

interface DateTimeSectionProps {
  control: Control<any>;
  getValues: UseFormGetValues<any>;
  setValue: UseFormSetValue<any>;
  isTablet: boolean;
  defaultDate?: Date;
  startTime: Date;
  endTime: Date;
  defaultStartTime: Date;
  defaultEndTime: Date;
  errors: FieldErrors<any>;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
  watch: any;
  peakTimeRecommendation?: string | null;
}

const DateTimeSection: React.FC<DateTimeSectionProps> = ({
  control,
  getValues,
  setValue,
  errors,
  defaultDate = new Date(),
  startTime,
  endTime,
  defaultStartTime,
  defaultEndTime,
  isTablet,
  setError,
  clearErrors,
  watch,
  peakTimeRecommendation,
}) => {
  const [activeField, setActiveField] = React.useState<string | null>(null);
  const [stateMinDate, setStateMinDate] = React.useState<Date | null>(null);

  React.useEffect(() => {
    startTime && startTime.setSeconds(0, 0);
    endTime && endTime.setSeconds(0, 0);

    if (startTime && endTime < startTime) {
      setError("endTime", {
        type: "manual",
        message: "End time must be after start time",
      });
    } else {
      clearErrors("endTime");
    }
  }, [startTime, endTime]);

  const minDate = watch("startTime");

  React.useEffect(() => {
    setStateMinDate(minDate);
  }, [minDate]);

  return (
    <View
      style={{
        marginBottom: errors["endTime"] ? 10 : 0,
        width: "100%",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          justifyContent: "flex-start",
          paddingBottom: 0,
          marginBottom: -20,
          width: "100%",
        }}
      >
        <InputDate
          control={control}
          label="Date & Time"
          name="date"
          required
          getValues={getValues}
          setValue={setValue}
          defaultValue={defaultDate}
          testingID="date_input"
          errors={errors}
          isActive={activeField}
          setActiveField={setActiveField}
        />
        <View style={styles.space} />
        <View
          style={{
            flexDirection: "row",
            flex: 1.4,
          }}
        >
          {peakTimeRecommendation ? (
            <View
              pointerEvents="none"
              testID="peak_time_recommendation"
              style={styles.peakTimeBadge}
            >
              <Feather
                name="trending-up"
                size={12}
                color={Colors.brand_dark}
              />
              <Text style={styles.peakTimeText}>Peak times:</Text>
              <Text style={styles.peakTimeText}>{peakTimeRecommendation}</Text>
            </View>
          ) : null}
          <InputDate
            control={control}
            label=""
            name="startTime"
            mode="time"
            getValues={getValues}
            setValue={setValue}
            defaultValue={defaultStartTime}
            testingID="start_time_input"
            errors={errors}
            isActive={activeField}
            setActiveField={setActiveField}
          />
          <View
            style={[
              styles.minus,
              {
                marginTop: Platform.OS === "web" ? -15 : 0,
              },
            ]}
          >
            <Feather name="minus" size={14} color={Colors.nuetral_grey_700} />
          </View>
          {startTime && (
            <InputDate
              control={control}
              label=""
              name="endTime"
              minDate={stateMinDate}
              mode="time"
              getValues={getValues}
              setValue={setValue}
              defaultValue={defaultEndTime}
              testingID="end_time_input"
              errors={errors}
              isActive={activeField}
              setActiveField={setActiveField}
            />
          )}
        </View>
      </View>
      {Platform.OS !== "web" && (
        <View style={styles.errorWrapper}>
          {errors["endTime"] ? (
            <Text style={styles.errorText}>{errors["endTime"]?.message}</Text>
          ) : (
            <View style={styles.space} />
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  space: {
    width: 10,
  },
  minus: {
    justifyContent: "center",
    paddingHorizontal: 5,
  },
  errorText: {
    color: "red",
  },
  errorWrapper: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    width: "100%",
    marginTop: -10,
  },
  peakTimeBadge: {
    position: "absolute",
    top: -2,
    left: 0,
    right: 0,
    minHeight: 20,
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
    backgroundColor: Colors.brand_lightest,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    zIndex: 1,
  },
  peakTimeText: {
    color: Colors.brand_dark,
    fontFamily: "NunitoBold",
    fontSize: 12,
    fontWeight: "700",
    lineHeight: 16,
  },
});

export default DateTimeSection;
